import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ensureLanguageLoaded, t } from '../../i18n/uiText';
import ChatInsightPanel from './ChatInsightPanel';
import {
  getStoredLanguageOrDefault,
  getStoredTranslationOrDefault,
  syncBibleLanguageFromTranslation,
} from '../../utils/bibleLocaleSync';
import { isWebAuthenticated, jsonHeadersWithAuth } from '../../utils/webAuth';
import { requestAuthPopup } from '../../utils/authPopup';
import { telegramService } from '../../services/telegram';
import { resolveAiLimitMessage } from '../../utils/aiLimitMessage';
import {
  addNewSession,
  computeTabStorageTitle,
  loadChatTabsState,
  persistChatTabsState,
  truncateTabText,
  updateSessionMessages,
  updateSessionSnapshot,
} from '../../utils/chatSessionsStorage';
import { ChatStreamMode } from '../../constants/chatStreamMode';
import { bibleService } from '../../services/bible';
import { BfIconNewChat } from '../../components/bible/bf/BfIcons';
import { formatReferenceLine, resolveReferenceHref } from '../../utils/chatReferenceLinks';

const API_BASE = import.meta.env.VITE_API_URL || 'https://verbum-api-production.up.railway.app/api';
const MAX_CHAT_MESSAGE_CHARS = 300;

/**
 * Если ответ уже содержит свёрнутый блок ссылок, разворачиваем в плоский HTML,
 * чтобы заново собрать его с актуальным `t('chatReferencesLabel')` при смене языка UI.
 */
function unwrapAssistantRefsCollapse(html) {
  const s = String(html || '');
  if (!s.includes('chat-refs-collapse')) return s;

  const detailsRe = /<details class="chat-refs-collapse"[^>]*>[\s\S]*?<\/details>/gi;
  const match = detailsRe.exec(s);
  if (!match) return s;

  const detailsBlock = match[0];
  const bodyMatch = detailsBlock.match(/<div class="chat-refs-body[^"]*">([\s\S]*?)<\/div>/i);
  if (!bodyMatch) return s;

  const bodyHtml = bodyMatch[1];
  const refLines = [];
  const refNoteRe =
    /class="bf-chat-refs__ref"[^>]*>([\s\S]*?)<\/div>\s*<p class="bf-chat-refs__note"[^>]*>([\s\S]*?)<\/p>/gi;
  let m;
  while ((m = refNoteRe.exec(bodyHtml))) {
    refLines.push(`${String(m[1] || '').trim()} — ${String(m[2] || '').trim()}`);
  }
  if (!refLines.length) {
    const lineRe = /<div class="chat-refs-line[^"]*">([\s\S]*?)<\/div>/gi;
    while ((m = lineRe.exec(bodyHtml))) {
      const chunk = String(m[1] || '').trim();
      if (chunk) refLines.push(chunk);
    }
  }
  if (!refLines.length) return s;

  const before = s.slice(0, match.index).replace(/(<br\/>\s*)+$/i, '');
  const refsJoin = refLines.join('<br/>');
  return before ? `${before}<br/>${refsJoin}` : refsJoin;
}

function parseIntOrNull(v) {
  if (v == null || v === '') return null;
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : null;
}

/** Как ChatStreamModeHelper.IsFollowUp (без Application — у него свой путь со snapshot). */
function isChatFollowUpMode(mode) {
  return (
    mode === ChatStreamMode.Simple ||
    mode === ChatStreamMode.KeyIdea ||
    mode === ChatStreamMode.Deep ||
    mode === ChatStreamMode.Example ||
    mode === ChatStreamMode.Faq
  );
}

/** Ввод из поля чата, не чип Insight / follow-up. */
function isFreeformUserMessage(m) {
  return m?.role === 'user' && !m.asFollowupChip;
}

/** Повтор того же чипа (Разбор / follow-up) до первого своего вопроса — не добавляем в ленту и снапшот. */
function shouldSkipRepeatVerseChip(messages, mode) {
  if (!Array.isArray(messages) || messages.some(isFreeformUserMessage)) return false;
  return messages.some(
    (m) =>
      m?.role === 'assistant' &&
      m.modeTag === mode &&
      (m.streaming || String(m.text || '').trim().length > 0)
  );
}

function dedupeTurns(items) {
  const seen = new Set();
  const result = [];
  for (const raw of items || []) {
    const text = String(raw || '').trim();
    if (!text) continue;
    if (seen.has(text)) continue;
    seen.add(text);
    result.push(text);
  }
  return result;
}

function buildCleanSnapshot(snapshot, messages, { forFollowUp = false } = {}) {
  if (!snapshot?.initialQuestion) return null;
  const freeformUsers = dedupeTurns(
    (messages || []).filter(isFreeformUserMessage).map((m) => String(m.text || '').trim())
  );
  const initialQuestion = freeformUsers[0] || snapshot.initialQuestion;
  const userTurns = freeformUsers.slice(1);
  const assistantTurns = dedupeTurns(
    (messages || [])
      .filter((m) => m?.role === 'assistant')
      .filter((m) => !m.streaming)
      .map((m) => String(m.text || '').trim())
  );

  const trimForFollowUp = (base) => {
    if (!forFollowUp) return base;
    const assistants = base.assistantTurns || [];
    if (assistants.length === 0) return base;
    const users = base.userTurns || [];
    const lastIndex = assistants.length - 1;
    const focusQuestion =
      lastIndex === 0 ? base.initialQuestion : users[lastIndex - 1] || base.initialQuestion;
    return {
      initialQuestion: focusQuestion,
      userTurns: [],
      assistantTurns: [assistants[lastIndex]],
    };
  };

  if (assistantTurns.length === 0) {
    const fallback = dedupeTurns(snapshot.assistantTurns || []);
    return trimForFollowUp({
      initialQuestion,
      userTurns,
      assistantTurns: fallback.slice(0, 1),
    });
  }

  return trimForFollowUp({
    initialQuestion,
    userTurns,
    assistantTurns,
  });
}

/**
 * Для компактного Insight: показываем ответ по кнопкам разбора до первого своего вопроса из чата.
 * Если после него снова нажали чип (Разобрать / follow-up), снова берём хвост цепочки — иначе
 * остался бы ответ на свободный вопрос вместо нового разбора.
 */
function getInsightSurfaceAssistant(messages, insightActiveMode = null) {
  const targetMode =
    insightActiveMode === 'application'
      ? ChatStreamMode.Application
      : insightActiveMode === 'explain'
        ? ChatStreamMode.VerseExplain
        : insightActiveMode === 'simple'
          ? ChatStreamMode.Simple
          : insightActiveMode === 'keyIdea'
            ? ChatStreamMode.KeyIdea
        : null;

  if (targetMode != null) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m?.role === 'assistant' && m.modeTag === targetMode) return m;
    }
  }

  const firstFreeIdx = messages.findIndex(isFreeformUserMessage);
  if (firstFreeIdx === -1) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'assistant') return messages[i];
    }
    return null;
  }
  let lastUser = null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'user') {
      lastUser = messages[i];
      break;
    }
  }
  if (lastUser?.asFollowupChip) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'assistant') return messages[i];
    }
    return null;
  }
  let last = null;
  for (let i = 0; i < firstFreeIdx; i += 1) {
    if (messages[i]?.role === 'assistant') last = messages[i];
  }
  return last;
}

/** Подпись вкладки: стих — ссылка; общий чат — «Новый вопрос» или обрезанный первый вопрос. */
function getChatTabLabel(session, t) {
  if (session?.verseReference?.trim()) {
    return truncateTabText(session.verseReference.trim(), 36);
  }
  const first = session.messages?.find((m) => m.role === 'user' && !m.isFollowUp);
  if (!first?.text?.trim()) return t('chatTabNewQuestion');
  return truncateTabText(first.text, 36);
}

export default function ChatStreamView({
  translation: translationProp,
  onClose,
  /** Глава уже проверила вход перед открытием чата — не показывать повторный запрос авторизации. */
  suppressAuthPrompt = false,
  messagePrefix = '',
  autoRunVerseExplain = false,
  autoRunMode = null,
  initialView = 'chat',
  verseSourceBookId = null,
  verseSourceChapter = null,
  verseSourceVerse = null,
  /** Пришли «задать свой вопрос» — сразу ставим курсор в поле. */
  focusComposer = false,
}) {
  const params = useParams();
  const translation = translationProp || params.translation || getStoredTranslationOrDefault();
  const isTelegramWebApp = telegramService.isTelegramWebApp;
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const endRef = useRef(null);
  const autoRunExplainTriggeredRef = useRef(false);
  const appliedChatLangForTabsRef = useRef(getStoredLanguageOrDefault());
  const closeStartedRef = useRef(false);
  const tabsDropdownRef = useRef(null);
  /** Один раз на комбинацию «префикс стиха + координаты», чтобы не дублировать вкладку при повторном эффекте. */
  const verseOpenLayoutKeyRef = useRef(null);
  const [query, setQuery] = useState('');
  const [chatTabs, setChatTabs] = useState(() => loadChatTabsState(getStoredLanguageOrDefault()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [presentationMode, setPresentationMode] = useState(initialView === 'insight' ? 'insight' : 'chat');
  const [hiddenInlineActionsBySession, setHiddenInlineActionsBySession] = useState({});
  const [tabsDropdownOpen, setTabsDropdownOpen] = useState(false);
  /** Режимы с «замком» на кнопке: разбор и применение. «Просто и ясно» — разовый пример, без подсветки как выбранный режим. */
  const [insightActiveMode, setInsightActiveMode] = useState(() => {
    if (initialView !== 'insight') return null;
    if (autoRunMode === 'simple') return 'simple';
    if (autoRunMode === 'application') return 'application';
    if (autoRunMode === 'keyIdea') return 'keyIdea';
    return 'explain';
  });
  /** В Insight текст стиха свёрнут в одну строку с …; клик разворачивает / сворачивает. */
  const [insightVerseExpanded, setInsightVerseExpanded] = useState(false);
  const [pendingMessagePrefix, setPendingMessagePrefix] = useState('');
  const [verseContextExpanded, setVerseContextExpanded] = useState(false);
  const [localeVersion, setLocaleVersion] = useState(0);
  const insightAutoloadedSessionsRef = useRef(new Set());
  const autoRunModeSessionsRef = useRef(new Set());
  const activeSession = useMemo(() => {
    if (!chatTabs) return null;
    return chatTabs.sessions.find((s) => s.id === chatTabs.activeSessionId) ?? null;
  }, [chatTabs]);

  const messages = activeSession?.messages ?? [];
  const snapshot = activeSession?.snapshot ?? null;

  /** Контекст стиха только у вкладки с привязкой (не у «Новый вопрос»). */
  const verseSessionContext = useMemo(() => {
    const ref = activeSession?.verseReference?.trim();
    if (!ref) return null;
    const raw = String(activeSession?.verseMessagePrefix || '').trim();
    if (raw) {
      const lines = raw.split('\n').map((x) => x.trim()).filter(Boolean);
      if (lines.length === 0) return { reference: ref, verse: '' };
      return {
        reference: lines[0] || ref,
        verse: lines.length > 1 ? lines.slice(1).join(' ') : '',
      };
    }
    return { reference: ref, verse: '' };
  }, [activeSession]);

  /**
   * Книга / глава / стих для API только у вкладки с привязкой к стиху.
   * Без привязки не подставляем пропсы главы — иначе «Новый вопрос» унаследует URL/выделение.
   */
  const buildVersePayload = useCallback(() => {
    const boundToPassage =
      Boolean(activeSession?.verseReference?.trim()) ||
      (parseIntOrNull(activeSession?.verseBookId) != null &&
        parseIntOrNull(activeSession?.verseChapter) != null &&
        parseIntOrNull(activeSession?.verseVerse) != null);
    if (!boundToPassage) return {};

    const bookId =
      parseIntOrNull(activeSession?.verseBookId) ?? parseIntOrNull(verseSourceBookId);
    const chapter =
      parseIntOrNull(activeSession?.verseChapter) ?? parseIntOrNull(verseSourceChapter);
    const verseNum =
      parseIntOrNull(activeSession?.verseVerse) ?? parseIntOrNull(verseSourceVerse);
    if (bookId == null || chapter == null || verseNum == null) return {};
    return {
      verseSourceBookId: bookId,
      verseSourceChapter: chapter,
      verseSourceVerse: verseNum,
    };
  }, [
    activeSession?.verseReference,
    activeSession?.verseBookId,
    activeSession?.verseChapter,
    activeSession?.verseVerse,
    verseSourceBookId,
    verseSourceChapter,
    verseSourceVerse,
  ]);

  /** @param {string | null} [sessionId] — если после await вкладка сменилась, передавай id сессии, начавшей запрос. */
  const patchMessages = useCallback((updater, sessionId = null) => {
    setChatTabs((prev) => {
      if (!prev) return prev;
      const id = sessionId ?? prev.activeSessionId;
      return updateSessionMessages(prev, id, updater);
    });
  }, []);

  const patchSnapshot = useCallback((next, sessionId = null) => {
    setChatTabs((prev) => {
      if (!prev) return prev;
      const id = sessionId ?? prev.activeSessionId;
      return updateSessionSnapshot(prev, id, next);
    });
  }, []);

  /** Самая свежая вкладка слева (последняя активность по updatedAt). */
  const sessionsSorted = useMemo(() => {
    if (!chatTabs?.sessions) return [];
    return [...chatTabs.sessions].sort((a, b) => b.updatedAt - a.updatedAt);
  }, [chatTabs]);

  const canSend = query.trim().length > 0 && !loading;

  const hasVerseMessagePrefix = Boolean(String(messagePrefix || '').trim());

  useEffect(() => {
    if (verseSessionContext || hasVerseMessagePrefix) return;
    inputRef.current?.focus();
  }, [verseSessionContext, hasVerseMessagePrefix]);

  useEffect(() => {
    if (!focusComposer) return undefined;
    const id = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 280);
    return () => clearTimeout(id);
  }, [focusComposer, messagePrefix]);

  useEffect(() => {
    const bump = () => setLocaleVersion((v) => v + 1);
    window.addEventListener('verbum:locale-updated', bump);
    const onStorage = (e) => {
      if (e.key === 'bibleLanguage') bump();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('verbum:locale-updated', bump);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    const code = (localStorage.getItem('bibleLanguage') || 'en').toLowerCase();
    void ensureLanguageLoaded(code);
  }, [translation, localeVersion]);

  // Маршрут /:translation/chat — запрос входа, если ещё не авторизованы. Из главы (suppressAuthPrompt) вход уже проверен.
  useEffect(() => {
    if (suppressAuthPrompt) return;
    const id = window.setTimeout(() => {
      if (!isWebAuthenticated()) {
        const next = `${window.location.pathname}${window.location.search}`;
        requestAuthPopup(next, true);
      }
    }, 400);
    return () => window.clearTimeout(id);
  }, [suppressAuthPrompt, navigate]);

  useEffect(() => {
    void syncBibleLanguageFromTranslation(translation);
  }, [translation]);

  /** Вкладки хранятся по языку UI (ru, en, …), не по id перевода Библии. */
  useEffect(() => {
    const code = getStoredLanguageOrDefault();
    if (appliedChatLangForTabsRef.current === code) return;
    appliedChatLangForTabsRef.current = code;
    setChatTabs(loadChatTabsState(code));
  }, [translation, localeVersion]);

  useEffect(() => {
    if (!chatTabs) return;
    const lang = getStoredLanguageOrDefault();
    const tid = window.setTimeout(() => {
      persistChatTabsState(lang, chatTabs);
    }, 400);
    return () => window.clearTimeout(tid);
  }, [chatTabs, localeVersion]);

  useEffect(() => {
    if (verseSessionContext || hasVerseMessagePrefix) return;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      inputRef.current.focus();
      const pos = inputRef.current.value.length;
      inputRef.current.setSelectionRange(pos, pos);
    });
  }, [messagePrefix, verseSessionContext, hasVerseMessagePrefix]);

  useEffect(() => {
    if (!chatTabs) return;
    const s = chatTabs.sessions.find((x) => x.id === chatTabs.activeSessionId);
    if (!s) return;
    const pending = s.messages?.length ? '' : String(s.verseMessagePrefix || '').trim();
    setPendingMessagePrefix(pending);
    setVerseContextExpanded(false);
  }, [chatTabs?.activeSessionId, chatTabs?.sessions, messages.length]);

  /**
   * Разбор по стиху: новая вкладка + посев стиха. Разбор из меню (без префикса): только сохранённые вкладки.
   */
  useLayoutEffect(() => {
    if (!hasVerseMessagePrefix) return;
    const fullPrefix = String(messagePrefix || '').trim();
    const lines = fullPrefix.split('\n').map((x) => x.trim()).filter(Boolean);
    const refLine = lines[0] || '';
    if (!refLine) return;

    const layoutKey = `${refLine}|${verseSourceBookId}|${verseSourceChapter}|${verseSourceVerse}|${translation}`;
    if (verseOpenLayoutKeyRef.current === layoutKey) return;
    verseOpenLayoutKeyRef.current = layoutKey;

    setChatTabs((prev) => {
      if (!prev) return prev;
      const nextBookId = parseIntOrNull(verseSourceBookId);
      const nextChapter = parseIntOrNull(verseSourceChapter);
      const nextVerse = parseIntOrNull(verseSourceVerse);

      const existing = prev.sessions.find((s) => {
        const sameRef = String(s.verseReference || '').trim() === refLine;
        const sameTranslation = String(s.verseTranslationId || '').trim() === String(translation || '').trim();
        const sameCoords =
          parseIntOrNull(s.verseBookId) === nextBookId &&
          parseIntOrNull(s.verseChapter) === nextChapter &&
          parseIntOrNull(s.verseVerse) === nextVerse;
        return sameRef && (sameTranslation || sameCoords);
      });

      if (existing) {
        return {
          ...prev,
          activeSessionId: existing.id,
          sessions: prev.sessions.map((s) => {
            if (s.id !== existing.id) return s;
            const refreshed = {
              ...s,
              verseReference: refLine,
              verseMessagePrefix: fullPrefix,
              verseBookId: nextBookId,
              verseChapter: nextChapter,
              verseVerse: nextVerse,
              verseTranslationId: translation,
            };
            refreshed.title = computeTabStorageTitle(refreshed, s.messages || []);
            return refreshed;
          }),
        };
      }

      const state = addNewSession(prev);
      const aid = state.activeSessionId;
      return {
        ...state,
        sessions: state.sessions.map((s) => {
          if (s.id !== aid) return s;
          const next = {
            ...s,
            verseReference: refLine,
            verseMessagePrefix: fullPrefix,
            verseBookId: nextBookId,
            verseChapter: nextChapter,
            verseVerse: nextVerse,
            verseTranslationId: translation,
          };
          next.title = computeTabStorageTitle(next, s.messages || []);
          return next;
        }),
      };
    });
  }, [
    hasVerseMessagePrefix,
    messagePrefix,
    translation,
    verseSourceBookId,
    verseSourceChapter,
    verseSourceVerse,
  ]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading]);

  /** После выхода из Insight `endRef` только что смонтирован — прокрутка вниз до следующего кадра. */
  useLayoutEffect(() => {
    if (presentationMode !== 'chat') return;
    requestAnimationFrame(() => {
      endRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' });
    });
  }, [presentationMode]);

  /** На вебе нет кнопки «глубже» (режим Deep) — она есть в Telegram. */
  const followUpButtons = [
    ...(isTelegramWebApp
      ? [{ mode: ChatStreamMode.Deep, emoji: '📖', label: t('chatFollowUpDeep') }]
      : []),
    { mode: ChatStreamMode.KeyIdea, emoji: '💡', label: t('chatFollowUpKeyThought') },
    { mode: ChatStreamMode.Simple, emoji: '✨', label: t('chatFollowUpSimple') },
  ];
  const hasChatStarted = messages.length > 0;
  const isInsightMode = presentationMode === 'insight';
  const isModalChat = Boolean(onClose);

  const showVerseBody =
    Boolean(verseSessionContext?.verse) &&
    (!hasChatStarted || verseContextExpanded || isModalChat);
  const verseBlockInteractive =
    Boolean(verseSessionContext?.verse) && hasChatStarted && !isModalChat;
  const verseContextHeaderOnly = verseBlockInteractive && !verseContextExpanded;

  useEffect(() => {
    setPresentationMode(initialView === 'insight' ? 'insight' : 'chat');
    if (initialView === 'insight') {
      if (autoRunMode === 'simple') setInsightActiveMode('simple');
      else if (autoRunMode === 'application') setInsightActiveMode('application');
      else if (autoRunMode === 'keyIdea') setInsightActiveMode('keyIdea');
      else setInsightActiveMode('explain');
    } else {
      setInsightActiveMode(null);
    }
  }, [initialView, messagePrefix, autoRunMode]);

  useEffect(() => {
    setInsightVerseExpanded(false);
  }, [verseSessionContext?.reference, verseSessionContext?.verse]);

  /** Ответ ассистента для компактного Insight (ветка разбора до своего вопроса из чата). */
  const insightSurfaceAssistant = useMemo(
    () => getInsightSurfaceAssistant(messages, insightActiveMode),
    [messages, insightActiveMode]
  );

  const [refBooks, setRefBooks] = useState([]);

  useEffect(() => {
    let cancelled = false;
    bibleService
      .getBooks(translation)
      .then((list) => {
        if (!cancelled) setRefBooks(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setRefBooks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [translation]);

  const handleReferenceLinkClick = useCallback(
    (e) => {
      const link = e.target.closest?.('a.bf-chat-refs__item--link');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('/')) return;
      e.preventDefault();
      e.stopPropagation();
      if (onClose) onClose();
      navigate(href);
    },
    [navigate, onClose]
  );

  const startParamToPath = (startParam) => {
    const raw = String(startParam || '').trim();
    if (!raw) return `/${translation}`;
    const parts = raw.split('_').filter(Boolean);
    if (parts.length === 0) return `/${translation}`;

    const isOldFormat = /^\d+$/.test(parts[0]);
    let tr = translation;
    let bookId;
    let chapter;
    let verse;
    let action = null;

    if (isOldFormat) {
      bookId = parts[0];
      chapter = parts[1];
      verse = parts[2];
      const last = parts[parts.length - 1];
      if (last === 'parallel' || last === 'interpretation' || last === 'image') action = last;
    } else {
      tr = parts[0] || translation;
      bookId = parts[1];
      chapter = parts[2];
      verse = parts[3];
      const last = parts[parts.length - 1];
      if (last === 'parallel' || last === 'interpretation' || last === 'image') action = last;
    }

    let path = `/${tr}`;
    if (bookId) path += `/${bookId}`;
    if (chapter) path += `/${chapter}`;
    if (verse) path += `/${verse}`;
    if (action && verse) path += `/${action}`;
    return path;
  };

  const normalizeAssistantHtml = useCallback((text) => {
    const source = String(text || '');
    const replacedAnchors = source.replace(
      /https:\/\/t\.me\/VerbumBibleBot\/app\?startapp=([A-Za-z0-9_]+)/gi,
      (_, startParam) => startParamToPath(startParam)
    );
    const withBasicMarkdown = replacedAnchors.replace(
      /\*\*(.+?)\*\*/g,
      '<strong>$1</strong>'
    );

    // Финальная нормализация: любые переносы -> <br/>
    let html = withBasicMarkdown
      .replace(/\\r\\n/g, '\n')
      .replace(/\\r/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\n/g, '<br/>');
    html = unwrapAssistantRefsCollapse(html);
    html = html.replace(
      /<\/(strong|b|em|a)>(?=[A-Za-z\u0400-\u04FF])/gi,
      (_, tag) => `</${tag}> `
    );

    const lines = html.split('<br/>');
    const referenceLines = [];
    const contentLines = [];
    /** Длина текста без тегов; длинные строки с цитатой стиха посередине — это толкование, не блок «Ссылки». */
    const plainLenApprox = (row) =>
      String(row || '')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim().length;
    const verseRefTokenRe =
      /(?:^|[\s>])(?:📖\s*)?(?:<strong>)?[\p{L}\d.\- ]{2,}\d+:\d+(?:<\/strong>)?/u;
    const MAX_REF_LINE_PLAIN_LEN = 260;
    const looksLikeReferenceLine = (row) => {
      const len = plainLenApprox(row);
      if (!len) return false;
      const hasVerseToken = verseRefTokenRe.test(row);
      const hasLink = row.includes('<a href=');
      if (hasLink && len <= MAX_REF_LINE_PLAIN_LEN) return true;
      if (hasVerseToken && len <= MAX_REF_LINE_PLAIN_LEN) return true;
      return false;
    };
    lines.forEach((line) => {
      const row = String(line || '').trim();
      if (!row) {
        contentLines.push(line);
        return;
      }
      if (looksLikeReferenceLine(row)) {
        referenceLines.push(row);
        return;
      }
      contentLines.push(line);
    });

    if (referenceLines.length === 0) return html;

    // Заголовок раздела, в котором были одни ссылки, после их сворачивания остаётся пустым — убираем.
    const isHeadingOnly = (row) => /^<(b|strong)>[^<]{2,60}<\/\1>:?$/i.test(String(row || '').trim());
    const keptLines = contentLines.filter((line, i) => {
      if (!isHeadingOnly(line)) return true;
      const next = contentLines.slice(i + 1).find((row) => String(row || '').trim());
      return next != null && !isHeadingOnly(next);
    });
    const contentHtml = keptLines
      .join('<br/>')
      .replace(/(<br\/>\s*){3,}/g, '<br/><br/>')
      .replace(/(<br\/>\s*)+$/g, '');
    const refsHtml = referenceLines
      .map((row) => formatReferenceLine(row, resolveReferenceHref(row, translation, refBooks)))
      .filter(Boolean)
      .join('');
    const refsLabel = `${t('chatReferencesLabel')} (${referenceLines.length})`;
    const collapseHtml = `<details class="chat-refs-collapse bf-chat-refs"><summary class="bf-chat-refs__summary"><span class="bf-chat-refs__chevron" aria-hidden="true"></span><span class="bf-chat-refs__title">${refsLabel}</span></summary><div class="chat-refs-body bf-chat-refs__list">${refsHtml}</div></details>`;
    return contentHtml ? `${contentHtml}<br/>${collapseHtml}` : collapseHtml;
  }, [translation, refBooks, t]);

  const consumeChat = useCallback(
    async (
      payload,
      userText,
      nextSnapshotBase = null,
      nextUserTurn = null,
      targetSessionId = null,
      options = null
    ) => {
      const shouldUpdateSnapshot = options?.updateSnapshot !== false;
      const response = await fetch(`${API_BASE}/chat`, {
        method: 'POST',
        headers: jsonHeadersWithAuth({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...payload,
          ...buildVersePayload(),
          isTelegram: telegramService.isTelegramWebApp,
          language: (localStorage.getItem('bibleLanguage') || 'en').toLowerCase(),
          translation,
        }),
      });
      const data = await response.json().catch(() => null);
      if (response.status === 401) {
        const next = `${window.location.pathname}${window.location.search}`;
        requestAuthPopup(next, true);
        throw new Error('__AUTH_REQUIRED__');
      }
      if (response.status === 429 && data?.code === 'DAILY_AI_LIMIT_REACHED') {
        throw new Error(resolveAiLimitMessage(data, t));
      }
      if (!response.ok) throw new Error(data?.error || t('error'));
      const assistantText = typeof data?.text === 'string' ? data.text : '';

      if (!assistantText.trim()) throw new Error(t('error'));

      const sid = targetSessionId;
      patchMessages(
        (prev) => {
          const next = [...prev];
          if (next.length > 0 && next[next.length - 1].role === 'assistant') {
            next[next.length - 1] = { ...next[next.length - 1], text: assistantText.trim(), streaming: false };
          }
          return next;
        },
        sid
      );

      if (!shouldUpdateSnapshot) {
        return;
      }

      if (nextSnapshotBase) {
        const existingUserTurns = Array.isArray(nextSnapshotBase.userTurns) ? nextSnapshotBase.userTurns : [];
        const updatedUserTurns = nextUserTurn ? [...existingUserTurns, nextUserTurn] : [...existingUserTurns];
        patchSnapshot(
          {
            initialQuestion: nextSnapshotBase.initialQuestion,
            userTurns: updatedUserTurns,
            assistantTurns: [...nextSnapshotBase.assistantTurns, assistantText.trim()],
          },
          sid
        );
      } else {
        patchSnapshot(
          {
            initialQuestion: userText,
            userTurns: [],
            assistantTurns: [assistantText.trim()],
          },
          sid
        );
      }
    },
    [translation, buildVersePayload, patchMessages, patchSnapshot]
  );

  const showAssistantErrorInBubble = useCallback((sessionId, message) => {
    if (!sessionId) return;
    const errorText = String(message || t('error')).trim();
    if (!errorText) return;
    patchMessages((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.role === 'assistant') {
        next[next.length - 1] = {
          ...last,
          text: errorText,
          streaming: false,
          isError: true,
        };
        return next;
      }
      next.push({ role: 'assistant', text: errorText, streaming: false, isError: true });
      return next;
    }, sessionId);
  }, [patchMessages, t]);

  const submitUserMessage = useCallback(
    async (rawText, forcedMode = null, msgOptions = null) => {
      const text = String(rawText || '').trim();
      if (!text) return;
      const tabId = activeSession?.id;
      if (!tabId) return;

      const chipLabel = msgOptions?.chipLabel != null ? String(msgOptions.chipLabel).trim() : '';
      // Режим-чип для UI-метки показываем только когда он выбран явно (кнопкой/чипом).
      // Обычное сообщение пользователя в существующем диалоге может идти в backend как Application,
      // но не должно визуально помечаться как «Применение».
      const resolvedModeTag = forcedMode ?? null;
      const cleanSnapshot = buildCleanSnapshot(snapshot, messages);

      const userPatch =
        msgOptions?.asFollowupChip === true
          ? {
              role: 'user',
              text,
              asFollowupChip: true,
              ...(msgOptions.chipEmoji ? { chipEmoji: msgOptions.chipEmoji } : {}),
              ...(chipLabel ? { chipLabel } : {}),
            }
          : { role: 'user', text };

      setError(null);
      setLoading(true);
      patchMessages(
        (prev) => [
          ...prev,
          userPatch,
          {
            role: 'assistant',
            text: '',
            streaming: true,
            modeTag: resolvedModeTag,
            showModeLabel: msgOptions?.asFollowupChip === true,
          },
        ],
        tabId
      );
      setQuery('');
      if (!verseSessionContext) {
        requestAnimationFrame(() => inputRef.current?.focus());
      }

      /** pendingMessagePrefix обновляется в useEffect позже посева стиха в сессию — при авто-«Разобрать» он ещё пуст. */
      const pendingTrim = pendingMessagePrefix.trim();
      const sessionPrefix = String(activeSession?.verseMessagePrefix || '').trim();
      const prefix =
        pendingTrim || (messages.length === 0 ? sessionPrefix : '');
      const vp = buildVersePayload();
      const hasVerseCoords =
        vp.verseSourceBookId != null &&
        vp.verseSourceChapter != null &&
        vp.verseSourceVerse != null;
      const newModeMessage =
        prefix && !hasVerseCoords
          ? `${prefix}\n\n${t('chatUserQuestionPrefix')} ${text}`
          : text;

      const verseOnlySnapshot =
        hasVerseCoords && !snapshot
          ? { initialQuestion: t('verseExplain'), userTurns: [], assistantTurns: [] }
          : null;
      const snapForFollowUp = snapshot || verseOnlySnapshot;
      const cleanSnapshotForFollowUp = snapForFollowUp
        ? buildCleanSnapshot(snapForFollowUp, messages, {
            forFollowUp: isChatFollowUpMode(forcedMode),
          })
        : null;

      try {
        const primaryMode = forcedMode ?? ChatStreamMode.Question;
        /* Раньше «snapshot && !prefix» всегда слало Application (1) — из‑за этого «Просто и ясно» шло не в Simple (5). */
        if (
          forcedMode != null &&
          (isChatFollowUpMode(forcedMode) || forcedMode === ChatStreamMode.Application)
        ) {
          const snapBody = cleanSnapshotForFollowUp
            ? {
                initialQuestion: cleanSnapshotForFollowUp.initialQuestion,
                userTurns: cleanSnapshotForFollowUp.userTurns || [],
                assistantTurns: cleanSnapshotForFollowUp.assistantTurns,
              }
            : snapForFollowUp
              ? {
                  initialQuestion: snapForFollowUp.initialQuestion,
                  userTurns: snapForFollowUp.userTurns || [],
                  assistantTurns: snapForFollowUp.assistantTurns,
                }
              : undefined;
          await consumeChat(
            {
              mode: forcedMode,
              message: text,
              ...(snapBody ? { snapshot: snapBody } : {}),
            },
            snapForFollowUp?.initialQuestion ?? text,
            snapForFollowUp ?? null,
            text,
            tabId,
            {
              updateSnapshot:
                forcedMode === ChatStreamMode.Application
                  ? msgOptions?.asFollowupChip !== true
                  : false,
            }
          );
        } else if (snapshot && forcedMode == null) {
          await consumeChat(
            {
              mode: ChatStreamMode.Question,
              message: text,
              snapshot: cleanSnapshot
                ? {
                    initialQuestion: cleanSnapshot.initialQuestion,
                    userTurns: cleanSnapshot.userTurns || [],
                    assistantTurns: cleanSnapshot.assistantTurns,
                  }
                : {
                    initialQuestion: snapshot.initialQuestion,
                    userTurns: snapshot.userTurns || [],
                    assistantTurns: snapshot.assistantTurns,
                  },
            },
            snapshot.initialQuestion,
            snapshot,
            text,
            tabId
          );
        } else {
          await consumeChat(
            { mode: primaryMode, message: newModeMessage },
            text,
            null,
            null,
            tabId
          );
        }
        if (prefix) setPendingMessagePrefix('');
      } catch (err) {
        if (err?.message === '__AUTH_REQUIRED__') return;
        const errorMessage = err?.message || t('error');
        showAssistantErrorInBubble(tabId, errorMessage);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [
      pendingMessagePrefix,
      snapshot,
      consumeChat,
      verseSessionContext,
      activeSession,
      activeSession?.id,
      messages.length,
      t,
      buildVersePayload,
      showAssistantErrorInBubble,
    ]
  );

  /** Первые действия по стиху в чате и в Insight: те же строки и тот же `mode`, что при пустом чате. */
  const verseQuickExplainSubmit = useCallback(() => {
    if (shouldSkipRepeatVerseChip(messages, ChatStreamMode.VerseExplain)) return;
    void submitUserMessage(t('verseExplain'), ChatStreamMode.VerseExplain, {
      asFollowupChip: true,
      chipEmoji: '\u{1F4D6}',
    });
  }, [submitUserMessage, t, messages]);

  const verseQuickApplicationSubmit = useCallback(() => {
    if (shouldSkipRepeatVerseChip(messages, ChatStreamMode.Application)) return;
    void submitUserMessage(t('chatApplyLifePrompt'), ChatStreamMode.Application, {
      asFollowupChip: true,
      chipEmoji: '\u{1F9ED}',
    });
  }, [submitUserMessage, t, messages]);

  const sendNewQuestion = async (e) => {
    e?.preventDefault();
    const text = query.trim();
    if (!text || loading) return;
    await submitUserMessage(text);
  };

  useEffect(() => {
    autoRunExplainTriggeredRef.current = false;
  }, [messagePrefix, autoRunVerseExplain]);

  useEffect(() => {
    autoRunModeSessionsRef.current.clear();
  }, [messagePrefix, autoRunMode]);

  useEffect(() => {
    if (!autoRunVerseExplain) return;
    if (!verseSessionContext) return;
    if (autoRunExplainTriggeredRef.current) return;
    if (loading || messages.length > 0) return;
    autoRunExplainTriggeredRef.current = true;
    void verseQuickExplainSubmit();
  }, [autoRunVerseExplain, verseSessionContext, loading, messages.length, verseQuickExplainSubmit]);

  useEffect(() => {
    if (autoRunMode === 'simple') return;
    if (autoRunMode) return;
    if (!isInsightMode || loading || !verseSessionContext) return;
    const sid = activeSession?.id;
    if (!sid || messages.length > 0) return;
    if (insightAutoloadedSessionsRef.current.has(sid)) return;
    insightAutoloadedSessionsRef.current.add(sid);
    setInsightActiveMode('explain');
    void verseQuickExplainSubmit();
  }, [
    autoRunMode,
    activeSession?.id,
    isInsightMode,
    loading,
    messages.length,
    verseQuickExplainSubmit,
    verseSessionContext,
  ]);

  useEffect(() => {
    if (!autoRunMode || !verseSessionContext || loading) return;
    const sid = activeSession?.id;
    if (!sid) return;
    const key = `${sid}:${autoRunMode}`;
    if (autoRunModeSessionsRef.current.has(key)) return;
    autoRunModeSessionsRef.current.add(key);
    if (autoRunMode === 'simple') {
      if (insightActiveMode !== 'simple') setInsightActiveMode('simple');
      void runFollowUp(ChatStreamMode.Simple);
      return;
    }
    if (autoRunMode === 'application') {
      setInsightActiveMode('application');
      void verseQuickApplicationSubmit();
      return;
    }
    if (autoRunMode === 'keyIdea') {
      setInsightActiveMode('keyIdea');
      void runFollowUp(ChatStreamMode.KeyIdea);
    }
  }, [
    autoRunMode,
    verseSessionContext,
    loading,
    activeSession?.id,
    messages.length,
    insightActiveMode,
    runFollowUp,
    t,
    setInsightActiveMode,
    verseQuickApplicationSubmit,
  ]);

  async function runFollowUp(mode) {
    const tabId = activeSession?.id;
    if (loading || !tabId) return;
    if (shouldSkipRepeatVerseChip(messages, mode)) return;
    const vp = buildVersePayload();
    const hasCoords =
      vp.verseSourceBookId != null &&
      vp.verseSourceChapter != null &&
      vp.verseSourceVerse != null;
    const effectiveSnapshot =
      snapshot ||
      (verseSessionContext && hasCoords
        ? { initialQuestion: t('verseExplain'), userTurns: [], assistantTurns: [] }
        : null);
    setError(null);
    setLoading(true);
    const btn = followUpButtons.find((b) => b.mode === mode);
    const selectedButton = btn?.label || String(mode);
    const chipEmoji = btn?.emoji;
    patchMessages(
      (prev) => [
        ...prev,
        {
          role: 'user',
          text: selectedButton,
          isFollowUp: true,
          asFollowupChip: true,
          ...(chipEmoji ? { chipEmoji } : {}),
        },
        { role: 'assistant', text: '', streaming: true, modeTag: mode, showModeLabel: true },
      ],
      tabId
    );
    try {
      const cleanSnap = effectiveSnapshot
        ? buildCleanSnapshot(effectiveSnapshot, messages, { forFollowUp: true })
        : null;
      const snapBody = cleanSnap
        ? {
            initialQuestion: cleanSnap.initialQuestion,
            userTurns: cleanSnap.userTurns || [],
            assistantTurns: cleanSnap.assistantTurns,
          }
        : effectiveSnapshot
          ? {
              initialQuestion: effectiveSnapshot.initialQuestion,
              userTurns: effectiveSnapshot.userTurns || [],
              assistantTurns: effectiveSnapshot.assistantTurns,
            }
          : undefined;
      await consumeChat(
        {
          mode,
          message: selectedButton,
          ...(snapBody ? { snapshot: snapBody } : {}),
        },
        effectiveSnapshot?.initialQuestion ?? selectedButton,
        effectiveSnapshot ?? null,
        selectedButton,
        tabId,
        { updateSnapshot: false }
      );
    } catch (err) {
      if (err?.message === '__AUTH_REQUIRED__') return;
      const errorMessage = err?.message || t('error');
      showAssistantErrorInBubble(tabId, errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  const startNewQuestion = () => {
    setQuery('');
    setError(null);
    setChatTabs((prev) => (prev ? addNewSession(prev) : prev));
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleSelectChatTab = (sessionId) => {
    if (!chatTabs || sessionId === chatTabs.activeSessionId) return;
    setQuery('');
    setError(null);
    setChatTabs((prev) => (prev ? { ...prev, activeSessionId: sessionId } : prev));
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleClose = useCallback(() => {
    if (closeStartedRef.current) return;
    closeStartedRef.current = true;

    const finish = () => {
      closeStartedRef.current = false;
      setIsClosing(false);
      if (onClose) onClose();
      else navigate(`/${translation}`);
    };

    if (isTelegramWebApp) {
      finish();
      return;
    }

    setIsClosing(true);
    window.setTimeout(finish, 220);
  }, [onClose, navigate, translation, isTelegramWebApp]);

  useEffect(() => {
    closeStartedRef.current = false;
    setIsClosing(false);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleClose]);

  useEffect(() => {
    if (!tabsDropdownOpen) return;
    const onPointerDown = (e) => {
      if (!tabsDropdownRef.current) return;
      if (tabsDropdownRef.current.contains(e.target)) return;
      setTabsDropdownOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [tabsDropdownOpen]);

  useEffect(() => {
    if (!tabsDropdownOpen) return;
    const onPointerDown = (e) => {
      if (!tabsDropdownRef.current) return;
      if (tabsDropdownRef.current.contains(e.target)) return;
      setTabsDropdownOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [tabsDropdownOpen]);

  const isInlineActionHidden = useCallback(
    (actionKey) => {
      const sid = activeSession?.id;
      if (!sid) return false;
      return Boolean(hiddenInlineActionsBySession[sid]?.[actionKey]);
    },
    [activeSession?.id, hiddenInlineActionsBySession]
  );

  const hideInlineAction = useCallback(
    (actionKey) => {
      const sid = activeSession?.id;
      if (!sid) return;
      setHiddenInlineActionsBySession((prev) => ({
        ...prev,
        [sid]: {
          ...(prev[sid] || {}),
          [actionKey]: true,
        },
      }));
    },
    [activeSession?.id]
  );

  const areAllInlineActionsHidden = useCallback(() => {
    return (
      isInlineActionHidden('simple') &&
      isInlineActionHidden('application')
    );
  }, [isInlineActionHidden]);

  const hasInlineModeAnswers = useMemo(
    () =>
      messages.some(
        (m) =>
          m?.role === 'assistant' &&
          (m.modeTag === ChatStreamMode.Simple ||
            m.modeTag === ChatStreamMode.KeyIdea ||
            m.modeTag === ChatStreamMode.Deep ||
            m.modeTag === ChatStreamMode.Application)
      ),
    [messages]
  );

  const openInsightsMode = useCallback(() => {
    if (!verseSessionContext) return;
    setInsightActiveMode('explain');
    setPresentationMode('insight');
    setInsightVerseExpanded(false);
  }, [verseSessionContext]);

  return (
    <div
      className={`chat-page-root chat-page-modal ${isTelegramWebApp ? 'chat-page-modal--no-open-anim chat-page-modal--tg' : ''} ${isClosing ? 'chat-page-closing' : ''} ${isInsightMode ? 'chat-page-modal--insight' : ''}`}
      style={{ fontFamily: 'var(--reading-ui-font)', color: '#334155' }}
      onClick={handleClose}
    >
      {!isTelegramWebApp && (
        <button
          type="button"
          className="chat-page-backdrop-close"
          aria-label={t('close')}
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
        >
          <svg aria-hidden="true" viewBox="0 0 20 20" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5 5l10 10M15 5L5 15"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
      <div className="chat-page-shell chat-page-shell-modal" onClick={(e) => e.stopPropagation()}>
        <header className="chat-modal-header">
          <div className="bible-modal-sheet-handle" aria-hidden="true" />
          {!isInsightMode && (
            <div className="chat-modal-header-row">
              {chatTabs && (
                <div className="chat-modal-header-add-slot">
                  <button
                    type="button"
                    className="chat-page-tab-add chat-page-tab-add--pill"
                    onClick={startNewQuestion}
                    aria-label={t('chatTabNew')}
                    title={t('chatTabNew')}
                  >
                    <span className="chat-page-tab-add-plus" aria-hidden="true">
                      <BfIconNewChat width={17} height={17} />
                    </span>
                  </button>
                </div>
              )}
              {chatTabs && (
                <div ref={tabsDropdownRef} className="chat-page-modal-tabs-strip">
                  <button
                    type="button"
                    className="chat-page-tabs-dropdown-trigger"
                    aria-haspopup="listbox"
                    aria-expanded={tabsDropdownOpen}
                    aria-label={t('chatTabsRowAria')}
                    onClick={() => setTabsDropdownOpen((v) => !v)}
                  >
                    <span className="chat-page-tabs-dropdown-label">
                      {getChatTabLabel(activeSession, t)}
                    </span>
                    <span className="chat-page-tabs-dropdown-caret" aria-hidden="true">
                      ▾
                    </span>
                  </button>
                  {tabsDropdownOpen && (
                    <div className="chat-page-tabs-dropdown-menu" role="listbox" aria-label={t('chatTabsRowAria')}>
                      {sessionsSorted.map((s) => {
                        const active = s.id === chatTabs.activeSessionId;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            role="option"
                            aria-selected={active}
                            className={`chat-page-tabs-dropdown-item${active ? ' is-active' : ''}`}
                            onClick={() => {
                              handleSelectChatTab(s.id);
                              setTabsDropdownOpen(false);
                            }}
                          >
                            {getChatTabLabel(s, t)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {!isTelegramWebApp && (
                <button
                  type="button"
                  className="chat-page-top-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  aria-label={t('close')}
                  title={t('close')}
                >
                  <span className="chat-page-top-close-icon" aria-hidden="true">
                    <svg viewBox="0 0 20 20" width="17" height="17" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M5 5l10 10M15 5L5 15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </button>
              )}
            </div>
          )}
        </header>

        <main className="chat-page-messages" onClick={handleReferenceLinkClick}>
        {isInsightMode && verseSessionContext && (
          <ChatInsightPanel
            t={t}
            verseSessionContext={verseSessionContext}
            insightVerseExpanded={insightVerseExpanded}
            setInsightVerseExpanded={setInsightVerseExpanded}
            insightSurfaceAssistant={insightSurfaceAssistant}
            loading={loading}
            normalizeAssistantHtml={normalizeAssistantHtml}
            insightActiveMode={insightActiveMode}
            setInsightActiveMode={setInsightActiveMode}
            followUpButtons={followUpButtons}
            snapshot={snapshot}
            runFollowUp={runFollowUp}
            verseQuickExplainSubmit={verseQuickExplainSubmit}
            verseQuickApplicationSubmit={verseQuickApplicationSubmit}
            setPresentationMode={setPresentationMode}
            inputRef={inputRef}
            onRequestClose={handleClose}
            hideCustomClose={isTelegramWebApp}
          />
        )}

        {!isInsightMode && (
          <>
        {verseSessionContext && (
          <div className="chat-page-verse-scroll">
            <div
              className={`chat-page-session-context${verseBlockInteractive ? ' chat-page-session-context--clickable' : ''}${verseContextHeaderOnly ? ' chat-page-session-context--header-only' : ''}`}
              onClick={
                verseBlockInteractive ? () => setVerseContextExpanded((v) => !v) : undefined
              }
            >
              <div className="chat-page-session-context-ref">
                {t('chatVerseContextLabel')} <strong>{verseSessionContext.reference}</strong>
              </div>
              {showVerseBody && (
                <div className="chat-page-session-context-verse">«{verseSessionContext.verse}»</div>
              )}
            </div>
            {messages.length === 0 && !loading && (
              <div className="chat-suggest" role="group" aria-label={t('chatSuggestLabel')}>
                <div className="chat-suggest__label">{t('chatSuggestLabel')}</div>
                {[
                  { key: 'main', label: t('chatSuggestMain'), text: t('verseExplain'), mode: ChatStreamMode.VerseExplain },
                  { key: 'simple', label: t('chatSuggestSimple'), text: t('chatFollowUpSimple'), mode: ChatStreamMode.Simple },
                  { key: 'apply', label: t('chatSuggestApply'), text: t('chatApplyLifePrompt'), mode: ChatStreamMode.Application },
                ].map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className="chat-suggest__item"
                    disabled={loading}
                    onClick={() =>
                      void submitUserMessage(s.text, s.mode, { asFollowupChip: true, chipLabel: s.label })
                    }
                  >
                    <span>{s.label}</span>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M7 17L17 7M9 7h8v8" />
                    </svg>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.length === 0 && !verseSessionContext && (
          <div className="chat-page-empty">
            {verseSessionContext ? (
              <>
                <p>{t('chatEmptyWithVerse')}</p>
                <p className="chat-page-empty-sub">{t('chatStreamingHint')}</p>
              </>
            ) : (
              <>
                <p>{t('chatEmptyGeneral')}</p>
                <p className="chat-page-empty-sub">{t('chatStreamingHint')}</p>
              </>
            )}
          </div>
        )}

        {messages.map((msg, idx) => {
          if (msg.role === 'user' && msg.asFollowupChip && !msg.chipLabel) return null;
          const askedAsQuestion = msg.role === 'assistant' && Boolean(messages[idx - 1]?.chipLabel);
          const modeLabel = msg.showModeLabel && !askedAsQuestion
            ? msg.modeTag === ChatStreamMode.VerseExplain
              ? t('verseExplain')
              : msg.modeTag === ChatStreamMode.Simple
                ? t('chatFollowUpSimple')
                : msg.modeTag === ChatStreamMode.KeyIdea
                  ? t('chatFollowUpKeyThought')
                  : msg.modeTag === ChatStreamMode.Deep
                    ? t('chatFollowUpDeep')
                  : msg.modeTag === ChatStreamMode.Application
                    ? t('application')
                    : null
            : null;
          const modeIcon = msg.showModeLabel
            ? msg.modeTag === ChatStreamMode.VerseExplain
              ? '📖'
              : msg.modeTag === ChatStreamMode.Simple
                ? '✨'
                : msg.modeTag === ChatStreamMode.KeyIdea
                  ? '💡'
                  : msg.modeTag === ChatStreamMode.Deep
                    ? '📖'
                  : msg.modeTag === ChatStreamMode.Application
                    ? '🧭'
                    : null
            : null;
          return (
            <div
              key={`${msg.role}-${idx}`}
              className={`chat-bubble-row ${msg.role === 'user' ? 'chat-bubble-row-user' : 'chat-bubble-row-assistant'}`}
            >
              {msg.role === 'assistant' ? (
                <div className={`chat-bubble chat-bubble-assistant${modeLabel ? ' chat-bubble-assistant--mode' : ''}`}>
                  {modeLabel ? (
                    <div className="chat-bubble-mode-label">
                      {modeIcon ? (
                        <span className="chat-bubble-mode-label-icon" aria-hidden="true">
                          {modeIcon}
                        </span>
                      ) : null}
                      <span>{modeLabel}</span>
                    </div>
                  ) : null}
                  {msg.text ? (
                    <span
                      className="chat-bubble-html"
                      dangerouslySetInnerHTML={{ __html: normalizeAssistantHtml(msg.text) }}
                    />
                  ) : (
                    <span className="chat-bubble-loading">{t('chatLoadingAnswer')}</span>
                  )}
                  {msg.streaming && <span className="chat-stream-caret" aria-hidden="true">▍</span>}
                </div>
              ) : (
                <div className="chat-bubble chat-bubble-user">{msg.chipLabel || msg.text}</div>
              )}
            </div>
          );
        })}

        <div ref={endRef} />
          </>
        )}
        </main>

        {!isInsightMode && (
        !loading &&
        snapshot &&
        messages.length > 0 &&
        messages[messages.length - 1].role === 'assistant' &&
        !hasInlineModeAnswers &&
        !areAllInlineActionsHidden() ? (
          <div className="chat-followup-row-inline" role="group" aria-label={t('verseActions')}>
            {isTelegramWebApp &&
              (() => {
                const deepBtn = followUpButtons.find((x) => x.mode === ChatStreamMode.Deep);
                if (!deepBtn || isInlineActionHidden('deep')) return null;
                return (
                  <button
                    key={deepBtn.mode}
                    type="button"
                    className="chat-followup-btn chat-followup-btn-inline"
                    onClick={() => {
                      hideInlineAction('deep');
                      runFollowUp(deepBtn.mode);
                    }}
                  >
                    <span className="chat-followup-btn-emoji" aria-hidden="true">
                      {deepBtn.emoji}
                    </span>
                    <span>{deepBtn.label}</span>
                  </button>
                );
              })()}
            {(() => {
              const simpleBtn = followUpButtons.find((x) => x.mode === ChatStreamMode.Simple);
              if (!simpleBtn || isInlineActionHidden('simple')) return null;
              return (
                <button
                  key={simpleBtn.mode}
                  type="button"
                  className="chat-followup-btn chat-followup-btn-inline"
                  onClick={() => {
                    hideInlineAction('simple');
                    runFollowUp(simpleBtn.mode);
                  }}
                >
                  <span className="chat-followup-btn-emoji" aria-hidden="true">
                    {simpleBtn.emoji}
                  </span>
                  <span>{simpleBtn.label}</span>
                </button>
              );
            })()}
            {!isInlineActionHidden('application') && (
              <button
                type="button"
                className="chat-followup-btn chat-followup-btn-inline"
                onClick={() => {
                  hideInlineAction('application');
                  void verseQuickApplicationSubmit();
                }}
              >
                <span className="chat-followup-btn-emoji" aria-hidden="true">
                  {'\u{1F9ED}'}
                </span>
                <span>{t('application')}</span>
              </button>
            )}
          </div>
        ) : null
        )}

        {!isInsightMode && (
        <form className="chat-page-composer" onSubmit={sendNewQuestion}>
          <div className="chat-composer-field">
            <input
              ref={inputRef}
              type="text"
              className="chat-composer-input"
              placeholder={
                verseSessionContext
                  ? t('chatComposerPlaceholderVerse')
                  : t('chatComposerPlaceholderGeneral')
              }
              value={query}
              onChange={(e) => setQuery(String(e.target.value || '').slice(0, MAX_CHAT_MESSAGE_CHARS))}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
                e.preventDefault();
                void sendNewQuestion();
              }}
              maxLength={MAX_CHAT_MESSAGE_CHARS}
            />
            <button type="submit" className="chat-composer-submit" disabled={!canSend} aria-label={t('chatSend')}>
              {loading ? (
                <span className="chat-composer-spinner" aria-hidden="true" />
              ) : (
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 19V5M5.5 11.5L12 5l6.5 6.5" />
                </svg>
              )}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
